import { CSSProperties, FC, useCallback, useMemo, useRef } from 'react';
import { TeamMember } from '@demind-inc/core';
import { CircularProgress, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { useRecoilState } from 'recoil';
import { SlotInfo } from 'react-big-calendar';
import { EventInteractionArgs } from 'react-big-calendar/lib/addons/dragAndDrop';
import { useTranslation } from 'react-i18next';

import './TeamDayView.scss';
import {
  useCreateEventMenuContext,
  useEventDetailsMenuContext,
  useTeamDay,
  selectedDateAtom,
} from '../../../data-access';
import { MinuteTickContext, useEventDragAndDrop, useMinuteTick } from '../../../hooks';
import {
  CalendarTimelineBackgroundEvent,
  EditEventMenu,
  MemoizedCalendar,
  RBCEvent,
  TemporaryEventMenu,
  transformCreatingEventToRBCEvent,
  transformEventToRBCEvent,
} from '../../CalendarView';
import {
  buildResourcesWithHiddenCalendar,
  buildTeamDayBackgroundEvents,
  buildTeamMemberLookup,
  getTeamMemberDisplayName,
  getTeamMemberInitials,
  getTeamMemberPhotoUrl,
  isCancelledTeamDayEvent,
  normalizeResourceId,
  normalizeTeamDayPhases,
} from '../utils';
import TeamDayEnergyLanes from '../TeamDayEnergyLanes';
import TeamDayCurrentTimeMarkers from '../TeamDayCurrentTimeMarkers';
import TeamDayToolbar from './TeamDayToolbar';
import TeamDayResourceHeader from './TeamDayResourceHeader';
import {
  TeamDayHiddenCalendarCards,
  TeamDayHiddenCalendarStyles,
  TeamDayHiddenCalendarSummary,
} from './TeamDayHiddenCalendars';
import type {
  ResourceScopedRBCEvent,
  TeamDayEventInteractionArgs,
  TeamDayResource,
  TeamDaySlotInfo,
} from './types';
import { formatDataDate } from '../../DataView/i18n';

interface TeamDayViewProps {
  teamId: string;
  members: TeamMember[];
  currentUserId: string;
  onOpenInviteModal: () => void;
}

const noop = () => undefined;

const TeamDayView: FC<TeamDayViewProps> = ({
  teamId,
  members,
  currentUserId,
  onOpenInviteModal,
}) => {
  const { t, i18n } = useTranslation();
  const calendarWrapRef = useRef<HTMLDivElement | null>(null);
  const calendarContainerRef = useRef<HTMLDivElement | null>(null);
  const [selectedDate, setSelectedDate] = useRecoilState(selectedDateAtom);
  const now = useMinuteTick();
  const { onEventDrop, onEventResize } = useEventDragAndDrop();
  const { creatingEvent, handleSelectNewEvent } = useCreateEventMenuContext();
  const { visibleMenuMode, selectedEvent, handleSelectEvent, clearSelectedEvent } =
    useEventDetailsMenuContext();
  const selectedDateString = selectedDate.format('YYYY-MM-DD');
  const {
    teamDay,
    dayMembers,
    isLoading: isTeamDayLoading,
    isFetching: isTeamDayFetching,
    error: teamDayError,
  } = useTeamDay({
    userId: currentUserId,
    teamId,
    date: selectedDateString,
  });

  const memberLookup = useMemo(() => buildTeamMemberLookup(members), [members]);
  const currentUserResourceIds = useMemo(() => {
    return new Set(
      dayMembers
        .filter((dayMember) => dayMember.userId === currentUserId && dayMember.isCalendarShared)
        .map((dayMember) => normalizeResourceId(dayMember.teamMemberId || dayMember.userId))
        .filter(Boolean)
    );
  }, [currentUserId, dayMembers]);
  const currentUserResourceId = useMemo(() => {
    return Array.from(currentUserResourceIds)[0] || null;
  }, [currentUserResourceIds]);
  const isCurrentUserResource = useCallback(
    (resourceId?: string | number) => {
      return currentUserResourceIds.has(normalizeResourceId(resourceId));
    },
    [currentUserResourceIds]
  );
  const isCurrentUserEvent = useCallback(
    (event: RBCEvent) => {
      return isCurrentUserResource((event as ResourceScopedRBCEvent).resourceId);
    },
    [isCurrentUserResource]
  );
  const isCurrentUserSlot = useCallback(
    (slot: SlotInfo) => {
      return isCurrentUserResource((slot as TeamDaySlotInfo).resourceId);
    },
    [isCurrentUserResource]
  );
  const dateLabel = useMemo(() => {
    const date = selectedDate.toDate();

    if (selectedDate.isSame(dayjs(), 'day')) {
      return t('team.day.todayDate', {
        date: formatDataDate(i18n.language, date, { month: 'short', day: 'numeric' }),
      });
    }

    return formatDataDate(i18n.language, date, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
  }, [i18n.language, selectedDate, t]);
  const teamDayErrorMessage = teamDayError instanceof Error ? teamDayError.message : null;

  const { resources, events, backgroundEvents } = useMemo(() => {
    const nextResources: TeamDayResource[] = [];
    const nextEvents: Array<RBCEvent & { resourceId: string }> = [];
    const nextBackgroundEvents: CalendarTimelineBackgroundEvent[] = [];

    dayMembers.forEach((dayMember, index) => {
      const resourceId = dayMember.teamMemberId || dayMember.userId;
      const member = memberLookup[dayMember.teamMemberId] || memberLookup[dayMember.userId];
      const memberName = getTeamMemberDisplayName(member, index, currentUserId, t);
      const memberInitialsSource = member?.name?.trim() || memberName;
      const sharedPhases = dayMember.isEnergyShared
        ? normalizeTeamDayPhases(dayMember.phases)
        : null;

      nextResources.push({
        id: resourceId,
        name: memberName,
        initials: getTeamMemberInitials(memberInitialsSource),
        photoUrl: getTeamMemberPhotoUrl(member),
        timezone: dayMember.timezone,
        isCalendarShared: dayMember.isCalendarShared,
        isEnergyShared: dayMember.isEnergyShared,
        phases: sharedPhases,
      });

      if (dayMember.isCalendarShared) {
        dayMember.events.forEach((event) => {
          if (isCancelledTeamDayEvent(event) || !event.start?.date || !event.end?.date) {
            return;
          }

          nextEvents.push({
            ...transformEventToRBCEvent(event),
            resourceId,
          });
        });
      }

      nextBackgroundEvents.push(
        ...buildTeamDayBackgroundEvents(resourceId, sharedPhases, dayMember.timezone)
      );
    });

    if (creatingEvent && currentUserResourceId) {
      nextEvents.push({
        ...transformCreatingEventToRBCEvent(creatingEvent),
        resourceId: currentUserResourceId,
      });
    }

    return {
      resources: nextResources,
      events: nextEvents,
      backgroundEvents: nextBackgroundEvents,
    };
  }, [creatingEvent, currentUserId, currentUserResourceId, dayMembers, memberLookup, t]);

  const hiddenCalendarResources = useMemo(
    () => buildResourcesWithHiddenCalendar(resources),
    [resources]
  );

  const handleNavigateDate = (direction: 'previous' | 'next') => {
    const dateStep = direction === 'previous' ? -1 : 1;
    setSelectedDate(selectedDate.add(dateStep, 'day'));
  };

  const handleResetToday = () => {
    setSelectedDate(dayjs());
  };

  const handleCalendarHorizontalScroll = useCallback(() => {
    const scrollLeft = calendarWrapRef.current?.scrollLeft ?? 0;
    calendarWrapRef.current?.style.setProperty('--team-day-scroll-left', `${scrollLeft}px`);
  }, []);

  const handleSelectTeamDayEvent = useCallback(
    (event: RBCEvent, nativeEvent?: Event | null) => {
      if (!isCurrentUserEvent(event)) {
        return;
      }

      handleSelectEvent(event, nativeEvent);
    },
    [handleSelectEvent, isCurrentUserEvent]
  );

  const handleSelectTeamDaySlot = useCallback(
    (slot: SlotInfo) => {
      if (!isCurrentUserSlot(slot)) {
        return;
      }

      handleSelectNewEvent({
        start: slot.start.toISOString(),
        end: slot.end.toISOString(),
      });
    },
    [handleSelectNewEvent, isCurrentUserSlot]
  );

  const handleTeamDayEventDrop = useCallback(
    (data: EventInteractionArgs<object>) => {
      const event = data.event as RBCEvent;
      const targetResourceId = (data as TeamDayEventInteractionArgs).resourceId;

      if (
        !isCurrentUserEvent(event) ||
        (targetResourceId && !isCurrentUserResource(targetResourceId))
      ) {
        return;
      }

      void onEventDrop(data);
    },
    [isCurrentUserEvent, isCurrentUserResource, onEventDrop]
  );

  const handleTeamDayEventResize = useCallback(
    (data: EventInteractionArgs<object>) => {
      if (!isCurrentUserEvent(data.event as RBCEvent)) {
        return;
      }

      void onEventResize(data);
    },
    [isCurrentUserEvent, onEventResize]
  );

  const renderContent = () => {
    if (isTeamDayLoading && !teamDay) {
      return (
        <div className="team-day-view__state" aria-live="polite">
          <CircularProgress size={26} className="team-day-view__spinner" />
          <Typography variant="body2" component="p" className="team-day-view__state-title">
            {t('team.day.loading')}
          </Typography>
        </div>
      );
    }

    if (teamDayErrorMessage) {
      return (
        <div className="team-day-view__state" role="alert">
          <Typography variant="body2" component="p" className="team-day-view__state-title">
            {t('team.day.loadError')}
          </Typography>
          <Typography variant="subtitle2" component="p" className="team-day-view__state-copy">
            {teamDayErrorMessage}
          </Typography>
        </div>
      );
    }

    if (!resources.length) {
      return (
        <div className="team-day-view__state" aria-live="polite">
          <Typography variant="body2" component="p" className="team-day-view__state-title">
            {t('team.day.noActiveMembers')}
          </Typography>
          <Typography variant="subtitle2" component="p" className="team-day-view__state-copy">
            {t('team.day.inviteToStart')}
          </Typography>
        </div>
      );
    }

    return (
      <div className="team-day-view__content">
        <div
          ref={calendarWrapRef}
          className="team-day-view__calendar-wrap"
          onScroll={handleCalendarHorizontalScroll}
        >
          <div
            className="team-day-view__calendar-stage"
            style={{ '--team-day-resource-count': resources.length } as CSSProperties}
          >
            <TeamDayHiddenCalendarStyles hiddenResources={hiddenCalendarResources} />
            <MinuteTickContext.Provider value={now}>
              <MemoizedCalendar
                ref={calendarContainerRef}
                date={selectedDate.toDate()}
                events={events}
                backgroundEventsOverride={backgroundEvents}
                peaksDipsBoundariesOverride={null}
                resources={resources as unknown as Record<string, unknown>[]}
                resourceHeader={TeamDayResourceHeader}
                resourceIdAccessor="id"
                resourceTitleAccessor="name"
                className="team-day-view__calendar"
                canInteractWithEvent={isCurrentUserEvent}
                canSelectSlot={isCurrentUserSlot}
                onEventDrop={handleTeamDayEventDrop}
                onEventResize={handleTeamDayEventResize}
                onDropEventFromOutside={noop}
                onSelectEvent={handleSelectTeamDayEvent}
                onSelectSlot={handleSelectTeamDaySlot}
                scrollToTime={selectedDate.hour(7).minute(0).toDate()}
              />
            </MinuteTickContext.Provider>
            <TeamDayEnergyLanes resources={resources} containerRef={calendarContainerRef} />
            <TeamDayCurrentTimeMarkers
              resources={resources}
              now={now}
              selectedDate={selectedDate}
              containerRef={calendarContainerRef}
            />
            <TeamDayHiddenCalendarCards
              hiddenResources={hiddenCalendarResources}
              resourceCount={resources.length}
            />
          </div>
        </div>
        <TeamDayHiddenCalendarSummary hiddenResources={hiddenCalendarResources} />
      </div>
    );
  };

  return (
    <>
      <main className="team-day-view">
        <TeamDayToolbar
          dateLabel={dateLabel}
          isFetching={isTeamDayFetching}
          onPrev={() => handleNavigateDate('previous')}
          onNext={() => handleNavigateDate('next')}
          onResetToday={handleResetToday}
          onOpenInviteModal={onOpenInviteModal}
        />
        {renderContent()}
      </main>
      <TemporaryEventMenu
        visible={!!selectedEvent && visibleMenuMode === 'temporary'}
        onClose={clearSelectedEvent}
      />
      <EditEventMenu
        visible={!!selectedEvent && visibleMenuMode === 'edit'}
        onClose={clearSelectedEvent}
      />
    </>
  );
};

export default TeamDayView;
